// Étape du dossier, en bloc compact dans la carte de titre : nom, rang et barre
// à segments. Clic = liste des étapes ; une étape = confirmer + date d’étape
// (étape courante = date seule).
import { useState } from 'react';
import { cn } from '../../lib/cn';
import {
  useDossierFrise,
  useTransitionFriseStep,
} from '../../hooks/queries/useDossiers';
import { type Dossier } from '../../services/dossiers/dossierService';
import { formatStageDate, todayInputValue } from '../../services/dossiers/stageDate';
import { Button } from '../ui/Button';
import { DossierFriseEditor } from './DossierFriseEditor';
import { Input } from '../ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface Props {
  dossier: Dossier;
  /** Autorise le clic-transition (mêmes droits que le kanban). */
  canTransition: boolean;
}

export function DossierStageStepper({ dossier, canTransition }: Props) {
  const { data: frise } = useDossierFrise(dossier.id);
  const transition = useTransitionFriseStep();
  const [pendingPosition, setPendingPosition] = useState<number | null>(null);
  const [effectiveOn, setEffectiveOn] = useState(todayInputValue);
  const [editingSteps, setEditingSteps] = useState(false);
  const [popOpen, setPopOpen] = useState(false);
  const busy = transition.isPending;

  const steps = frise?.steps ?? [];
  const currentPosition = frise?.position ?? 1;
  const current = steps.find((step) => step.position === currentPosition);
  const dateOnly = pendingPosition !== null && pendingPosition === currentPosition;
  const pending = steps.find((step) => step.position === pendingPosition);

  function handleClick(position: number) {
    if (!canTransition || busy) return;
    if (position === currentPosition && current?.effectiveOn) {
      setEffectiveOn(current.effectiveOn);
    } else {
      setEffectiveOn(todayInputValue());
    }
    setPendingPosition(position);
  }

  function confirmTransition() {
    if (pendingPosition === null || !effectiveOn) return;
    transition.mutate(
      { id: dossier.id, position: pendingPosition, effectiveOn },
      { onSettled: () => setPendingPosition(null) },
    );
  }

  const pendingLabel = pending?.label ?? '';

  const previous = steps.filter((step) => step.position < currentPosition).at(-1);
  const next = steps.find((step) => step.position > currentPosition);
  const currentIndex = steps.findIndex((step) => step.position === currentPosition);

  function ask(position: number) {
    setPopOpen(false);
    handleClick(position);
  }

  return (
    <>
    {editingSteps && (
      <DossierFriseEditor
        dossierId={dossier.id}
        steps={steps}
        currentPosition={currentPosition}
        onClose={() => setEditingSteps(false)}
      />
    )}
    <Popover open={popOpen} onOpenChange={setPopOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="dialog"
          title="Voir les étapes de ce dossier"
          className="flex w-[210px] flex-shrink-0 flex-col gap-1.5 rounded-[9px] border border-[#e6e2d8] bg-[#fbfaf6] px-3 pb-[9px] pt-[7px] text-left outline-none transition-colors hover:border-cime-gold/60 focus-visible:border-cime-gold/60 data-[state=open]:border-cime-gold/60"
        >
          <span className="flex items-baseline gap-1.5 text-[13px]">
            <small className="text-[10px] font-bold uppercase tracking-[0.08em] text-neutral-400">Étape</small>
            <b className="min-w-0 truncate font-semibold text-cime-primary">{current?.label ?? '—'}</b>
            <span className="ml-auto text-[11px] tabular-nums text-neutral-400">
              {steps.length > 0 ? `${currentIndex + 1}/${steps.length}` : ''}
            </span>
          </span>
          <span className="grid grid-flow-col auto-cols-fr gap-[3px]" aria-hidden>
            {steps.map((step) => (
              <span
                key={step.position}
                className={cn(
                  'h-1 rounded-full',
                  step.position < currentPosition
                    ? 'bg-cime-primary'
                    : step.position === currentPosition
                      ? 'bg-cime-gold'
                      : 'bg-neutral-200',
                )}
              />
            ))}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[270px] rounded-xl border-neutral-200 bg-white px-3.5 pb-3.5 pt-3 shadow-[0_14px_36px_rgba(31,40,35,0.14)]"
      >
        <h5 className="mb-2 text-[10px] font-bold uppercase tracking-[0.1em] text-neutral-400">
          Étapes de la procédure
        </h5>
        <ol className="mb-3" aria-label="Étapes de la procédure">
          {steps.map((step, i) => {
            const done = step.position < currentPosition;
            const isCurrent = step.position === currentPosition;
            return (
              <li key={step.position} className="relative">
                {i < steps.length - 1 ? (
                  <span aria-hidden className="absolute left-[4.5px] top-5 h-3 w-px bg-neutral-200" />
                ) : null}
                <button
                  type="button"
                  disabled={!canTransition}
                  onClick={() => ask(step.position)}
                  className={cn(
                    'flex w-full items-center gap-2.5 py-[5px] text-left text-[13px]',
                    isCurrent
                      ? 'font-semibold text-cime-text-primary'
                      : done
                        ? 'text-cime-text-muted'
                        : 'text-neutral-400',
                    canTransition && 'hover:text-cime-primary',
                  )}
                >
                  <i
                    aria-hidden
                    className={cn(
                      'h-2.5 w-2.5 flex-shrink-0 rounded-full border-2',
                      isCurrent
                        ? 'border-cime-gold bg-cime-gold shadow-[0_0_0_3px_rgba(201,168,76,0.2)]'
                        : done
                          ? 'border-cime-primary bg-cime-primary'
                          : 'border-neutral-300 bg-white',
                    )}
                  />
                  <span className="min-w-0 truncate">{step.label}</span>
                  {step.effectiveOn ? (
                    <em className="ml-auto flex-shrink-0 text-xs font-normal not-italic text-neutral-400">
                      {formatStageDate(step.effectiveOn)}
                    </em>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ol>
        {canTransition ? (
          <>
            <div className="flex flex-col gap-1.5">
              {previous ? (
                <Button type="button" variant="secondary" size="sm" className="w-full justify-center" onClick={() => ask(previous.position)}>
                  Revenir à « {previous.label} »
                </Button>
              ) : null}
              {next ? (
                <Button type="button" variant="primary" size="sm" className="w-full justify-center" onClick={() => ask(next.position)}>
                  Passer à « {next.label} »
                </Button>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => {
                setPopOpen(false);
                setEditingSteps(true);
              }}
              className="mt-2.5 flex w-full items-center justify-center gap-1.5 border-t border-neutral-100 pt-1.5 text-xs font-semibold text-cime-primary hover:underline"
            >
              <img src="/icons/pen.svg" alt="" className="h-3 w-3" />
              Modifier les étapes et leurs dates
            </button>
          </>
        ) : null}
      </PopoverContent>
    </Popover>

    <Dialog
      open={pendingPosition !== null}
      onOpenChange={(open) => {
        if (!open && !busy) setPendingPosition(null);
      }}
    >
      <DialogContent className="sm:max-w-md bg-white rounded-cime border-neutral-200">
        <DialogHeader>
          <DialogTitle className="text-cime-text-primary">
            {dateOnly ? "Date d'étape" : "Changer d'étape"}
          </DialogTitle>
          <DialogDescription className="text-cime-text-muted">
            {dateOnly
              ? `Modifier la date de l'étape « ${pendingLabel} ».`
              : `Passer le dossier à l'étape « ${pendingLabel} » ?`}
          </DialogDescription>
        </DialogHeader>
        <Input
          type="date"
          label="Date d'étape"
          value={effectiveOn}
          onChange={(e) => setEffectiveOn(e.target.value)}
        />
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => setPendingPosition(null)}
          >
            Annuler
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            isLoading={busy}
            disabled={!effectiveOn}
            onClick={confirmTransition}
          >
            Confirmer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
